// Password-reset flow via Supabase Auth.
//
// * No query params → show email-request form.
// * After supabase sends the reset link and the user clicks it, Supabase
//   redirects back here with type=recovery in the URL hash. App.tsx detects
//   the PASSWORD_RECOVERY event via onAuthStateChange and shows this screen;
//   the complete form calls supabase.auth.updateUser({ password }).

import React, { useState } from 'react';

import { supabase } from '../api/supabase';
import Logo from '../components/brand/Logo';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100dvh', background: 'var(--parchment)',
      paddingTop: 56, paddingBottom: 32,
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <Logo variant="lockup" height={96} />
      </div>
      {children}
    </div>
  );
}

interface Props {
  onDone: () => void;
  /** When true, show the new-password form (user came from the reset link). */
  isRecovery?: boolean;
}

export function PasswordResetScreen({ onDone, isRecovery = false }: Props) {
  if (isRecovery) return <CompleteForm onDone={onDone} />;
  return <RequestForm onDone={onDone} />;
}

// ── Request form ──────────────────────────────────────────────────────────
function RequestForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/password-reset`,
      });
    } finally {
      // Always show "sent" — never reveal whether the email is registered.
      setSent(true);
      setBusy(false);
    }
  };

  return (
    <Shell>
      <div style={cardStyle}>
        <h1 style={titleStyle}>Passwort vergessen?</h1>
        {sent ? (
          <>
            <p style={bodyStyle}>
              Wenn diese Email registriert ist, schicken wir dir gleich einen Link zum
              Zurücksetzen. Bitte prüfe dein Postfach (und den Spam-Ordner).
            </p>
            <button onClick={onDone} style={primaryBtn}>Zurück zum Login</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <p style={bodyStyle}>Gib deine Email an — wir senden dir einen Link zum Zurücksetzen.</p>
            <label style={labelStyle} htmlFor="pw-reset-email">Email</label>
            <input id="pw-reset-email" type="email" autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              style={inputStyle} placeholder="du@beispiel.de" required />
            <button type="submit" disabled={busy || !email.trim()} style={primaryBtn}>
              {busy ? 'Sende…' : 'Link anfordern'}
            </button>
            <button type="button" onClick={onDone} style={backLinkBtn}>
              ← Zurück zum Login
            </button>
          </form>
        )}
      </div>
    </Shell>
  );
}

// ── Complete form (shown after user clicks the reset link) ────────────────
function CompleteForm({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Mindestens 8 Zeichen.'); return; }
    if (password !== confirm) { setError('Die Passwörter stimmen nicht überein.'); return; }
    setBusy(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) { setError(updateErr.message); return; }
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Passwort gespeichert</h1>
          <p style={bodyStyle}>Du kannst dich jetzt mit deinem neuen Passwort einloggen.</p>
          <button onClick={onDone} style={primaryBtn}>Zum Login</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
    <div style={cardStyle}>
      <h1 style={titleStyle}>Neues Passwort setzen</h1>
      <form onSubmit={submit}>
        <label style={labelStyle} htmlFor="pw-new">Neues Passwort</label>
        <input id="pw-new" type="password" autoComplete="new-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          style={inputStyle} minLength={8} required />
        <label style={labelStyle} htmlFor="pw-confirm">Nochmal eingeben</label>
        <input id="pw-confirm" type="password" autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          style={inputStyle} minLength={8} required />
        {error && <p style={{ color: 'var(--wrong)', marginBottom: 12, fontSize: 13 }}>{error}</p>}
        <button type="submit" disabled={busy} style={primaryBtn}>
          {busy ? 'Speichere…' : 'Passwort speichern'}
        </button>
        <button type="button" onClick={onDone} style={backLinkBtn}>
          ← Zurück zum Login
        </button>
      </form>
    </div>
    </Shell>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
const cardStyle: React.CSSProperties = {
  maxWidth: 480, margin: '80px auto', padding: 32,
  background: 'var(--card)', borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
};
const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--display)', fontSize: 28, marginBottom: 16, color: 'var(--ink)',
};
const bodyStyle: React.CSSProperties = {
  fontFamily: 'var(--body)', fontSize: 16, lineHeight: 1.5, color: 'var(--ink)', marginBottom: 16,
};
const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em', color: 'var(--ink-muted)',
  textTransform: 'uppercase', display: 'block', marginBottom: 6, marginTop: 12,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', fontSize: 16, fontFamily: 'var(--body)',
  border: '1.5px solid rgba(60,50,40,0.18)', borderRadius: 8,
  background: 'var(--card)', color: 'var(--ink)', boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  marginTop: 20, width: '100%', padding: '12px 16px', fontSize: 16,
  fontFamily: 'var(--body)', fontWeight: 600, background: 'var(--amber)',
  color: 'var(--ink)', border: 'none', borderRadius: 8, cursor: 'pointer',
};
const backLinkBtn: React.CSSProperties = {
  marginTop: 14, width: '100%', padding: '8px 16px',
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em',
  color: 'var(--ink-muted)', textTransform: 'uppercase',
};
